import React, { useState, useCallback, useMemo } from 'react';
import {
    Box, Title, Text, Paper, SimpleGrid, Button, Accordion, Badge, Divider, Alert,
    Loader, TextInput, Table,
    Group, Stack
} from '@mantine/core';
import {
    IconChevronDown,
    IconPlayerPlay,
    IconCircleCheck,
    IconX,
    IconInfoCircle,
    IconRefresh,
    IconSettings,
    IconBug,
    IconGauge,
    IconDatabase,
    IconAlertCircle
} from '@tabler/icons-react';
import type { ScimUserService } from '../../services/ScimUserService';
import type { ScimSchemaService } from '../../services/ScimSchemaService';
import CentralizedSchemaService from '../../services/CentralizedSchemaService';
import type { ScimEntityService } from '../../services/ScimEntityService';
import { entityService, userService, schemaService } from '../../services/entityServiceSingleton';
import { ScimTestingService } from '../../services/ScimTestingService';
import { ScimAuthService } from '../../services/ScimAuthService';
import { corsApiService } from '../../services/CorsApiService';
import { getScimConfig } from '../../utils/envUtils';
import entitlementsService from '../../services/EntitlementsService';
import { getUserSettingsService } from '../../services/UserSettingsService';
import { formatTimeShort } from '../../utils/dateTimeUtils';

// Test result interfaces
interface TestResult {
    id: string;
    name: string;
    status: 'pending' | 'running' | 'passed' | 'failed' | 'skipped';
    duration?: number;
    error?: string;
    result?: any;
    timestamp?: string;
}

interface TestCategory {
    id: string;
    name: string;
    description: string;
    icon: React.ReactNode;
    tests: TestDefinition[];
}

interface TestDefinition {
    id: string;
    name: string;
    description: string;
    category: string;
    requiredParams?: string[];
    runTest: (services: {
        userService: ScimUserService;
        schemaService: ScimSchemaService;
        entityService: ScimEntityService;
        testingService: ScimTestingService;
        authService: ScimAuthService;
        corsApiService: typeof corsApiService;
    }, params?: any) => Promise<any>;
}

export const ScimApiTesting: React.FC = () => {
    const [testResults, setTestResults] = useState<TestResult[]>([]);
    const [runningTests, setRunningTests] = useState<Set<string>>(new Set());
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
    const [testParams, setTestParams] = useState<Record<string, any>>({});

    // Initialize services
    const testingService = useMemo(() => new ScimTestingService(), []);
    const authService = useMemo(() => new ScimAuthService(), []);

    // Test categories with comprehensive test definitions
    const testCategories: TestCategory[] = useMemo(() => [
        {
            id: 'authentication',
            name: 'Authentication & Configuration',
            description: 'Service configuration validation, API/Mock mode switching, authentication token handling',
            icon: <IconDatabase size={20} />,
            tests: [
                {
                    id: 'config-validation',
                    name: 'Service Configuration Validation',
                    description: 'Validate SCIM service configuration and endpoints',
                    category: 'authentication',
                    runTest: async () => {
                        try {
                            const token = await ScimAuthService.getAuthToken();
                            return {
                                hasAuthService: true,
                                hasToken: !!token,
                                timestamp: new Date().toISOString()
                            };
                        } catch (error) {
                            return {
                                hasAuthService: true,
                                hasToken: false,
                                error: error instanceof Error ? error.message : 'Unknown error',
                                timestamp: new Date().toISOString()
                            };
                        }
                    }
                },
                {
                    id: 'auth-token',
                    name: 'Authentication Token Handling',
                    description: 'Test authentication token acquisition and validation',
                    category: 'authentication',
                    runTest: async () => {
                        try {
                            const token = await ScimAuthService.getAuthToken();
                            return { hasToken: !!token, tokenLength: token?.length || 0 };
                        } catch (error) {
                            throw new Error(`Authentication failed: ${error}`);
                        }
                    }
                },
                {
                    id: 'entities-me',
                    name: 'Get Current User (entities/me)',
                    description: 'Test the entities/me endpoint to get current authenticated user data',
                    category: 'authentication',
                    runTest: async () => {
                        try {
                            const token = await ScimAuthService.getAuthToken();
                            if (!token) {
                                throw new Error('No authentication token available');
                            }

                            const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://dcps-core-api-dev.azurewebsites.net/api/scim/v2';
                            const response = await fetch(`${baseURL}/entities/me`, {
                                method: 'GET',
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json',
                                    'Accept': 'application/json'
                                }
                            });

                            if (!response.ok) {
                                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }

                            const data = await response.json();
                            return {
                                success: true,
                                userData: data,
                                status: response.status,
                                timestamp: new Date().toISOString()
                            };
                        } catch (error) {
                            throw new Error(`entities/me failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
                        }
                    }
                }
            ]
        },
        {
            id: 'user-management',
            name: 'User Management',
            description: 'Basic user retrieval, pagination, filtering, sorting, search, updates, and deletion',
            icon: <IconBug size={20} />,
            tests: [
                {
                    id: 'basic-users',
                    name: 'Basic User Retrieval',
                    description: 'Test basic user list retrieval',
                    category: 'user-management',
                    runTest: async (services) => {
                        const users = await services.userService.getUsers(1);
                        return { userCount: users.totalResults, users: users.users?.slice(0, 3) };
                    }
                },
                {
                    id: 'user-pagination',
                    name: 'User Pagination',
                    description: 'Test user pagination functionality',
                    category: 'user-management',
                    runTest: async (services, params) => {
                        const pageSize = params?.pageSize || 10;
                        const page1 = await services.userService.getUsers(1);
                        const page2 = await services.userService.getUsers(2);
                        return {
                            page1Count: page1.totalResults,
                            page2Count: page2.totalResults,
                            pageSize
                        };
                    },
                    requiredParams: ['pageSize']
                },
                {
                    id: 'user-filtering',
                    name: 'User Filtering by Entity Type',
                    description: 'Test user filtering by entity type',
                    category: 'user-management',
                    runTest: async (services, params) => {
                        const entityType = params?.entityType || 'Employee';
                        const filter = `EntityType eq "${entityType}"`;
                        const users = await services.userService.getUsers(1, filter);
                        return {
                            entityType,
                            filter,
                            userCount: users.totalResults,
                            users: users.users?.slice(0, 3)
                        };
                    },
                    requiredParams: ['entityType']
                },
                {
                    id: 'user-search',
                    name: 'User Search (Advanced)',
                    description: 'Test advanced user search functionality',
                    category: 'user-management',
                    runTest: async (services, params) => {
                        const searchTerm = params?.searchTerm || 'john';
                        const users = await services.userService.searchUsers(searchTerm);
                        return {
                            searchTerm,
                            userCount: users.totalResults,
                            users: users.users?.slice(0, 3)
                        };
                    },
                    requiredParams: ['searchTerm']
                }
            ]
        },
        {
            id: 'schema-management',
            name: 'Schema Management',
            description: 'SCIM schema retrieval and validation',
            icon: <IconSettings size={20} />,
            tests: [
                {
                    id: 'schema-entities-retrieval',
                    name: 'SCIM Schema Entities Retrieval',
                    description: 'Test SCIM schema entities retrieval',
                    category: 'schema-management',
                    runTest: async (services) => {
                        const schemaEntities = await services.schemaService.getSchemaEntities();
                        return {
                            entityCount: schemaEntities.totalResults,
                            entities: schemaEntities.Resources?.slice(0, 3)
                        };
                    }
                },
                {
                    id: 'schema-attributes-retrieval',
                    name: 'SCIM Schema Attributes Retrieval',
                    description: 'Test SCIM schema attributes retrieval',
                    category: 'schema-management',
                    runTest: async (services) => {
                        const schemaAttributes = await services.schemaService.getSchemaAttributes();
                        return {
                            attributeCount: schemaAttributes.totalResults,
                            attributes: schemaAttributes.Resources?.slice(0, 3)
                        };
                    }
                },
                {
                    id: 'schema-validation',
                    name: 'Schema Validation',
                    description: 'Validate SCIM schema structure',
                    category: 'schema-management',
                    runTest: async (services) => {
                        const [schemaEntities, schemaAttributes] = await Promise.all([
                            services.schemaService.getSchemaEntities(),
                            services.schemaService.getSchemaAttributes()
                        ]);

                        const validEntities = schemaEntities.Resources?.filter(e => e.DisplayName && e.Id) || [];
                        const validAttributes = schemaAttributes.Resources?.filter(a => a.DisplayName && a.Type) || [];

                        return {
                            totalEntities: schemaEntities.totalResults,
                            validEntities: validEntities.length,
                            totalAttributes: schemaAttributes.totalResults,
                            validAttributes: validAttributes.length,
                            invalidEntities: (schemaEntities.Resources?.length || 0) - validEntities.length,
                            invalidAttributes: (schemaAttributes.Resources?.length || 0) - validAttributes.length
                        };
                    }
                }
            ]
        },
        {
            id: 'resource-types',
            name: 'Resource Types',
            description: 'Resource type retrieval and validation',
            icon: <IconSettings size={20} />,
            tests: [
                {
                    id: 'resource-types-retrieval',
                    name: 'Schema Entity Type Retrieval',
                    description: 'Test schema entity type retrieval (replaces ResourceTypes)',
                    category: 'resource-types',
                    runTest: async () => {
                        const centralizedService = CentralizedSchemaService.getInstance();
                        const schemaData = await centralizedService.getSchemaData();
                        const schemas = schemaData.schemas ?? [];
                        return {
                            typeCount: schemas.length,
                            types: schemas.slice(0, 3)
                        };
                    }
                },
                {
                    id: 'resource-types-validation',
                    name: 'Schema Entity Type Validation',
                    description: 'Validate schema entity type structure',
                    category: 'resource-types',
                    runTest: async () => {
                        const centralizedService = CentralizedSchemaService.getInstance();
                        const schemaData = await centralizedService.getSchemaData();
                        const schemas = schemaData.schemas ?? [];
                        const validTypes = schemas.filter((t: { id?: string; name?: string }) => t.id && t.name);
                        return {
                            totalTypes: schemas.length,
                            validTypes: validTypes.length,
                            invalidTypes: schemas.length - validTypes.length
                        };
                    }
                }
            ]
        },
        {
            id: 'locality-management',
            name: 'Locality Management',
            description: 'Schools, buildings, classes, and responsibility centers retrieval and pagination',
            icon: <IconBug size={20} />,
            tests: [
                {
                    id: 'schools-retrieval',
                    name: 'Schools Retrieval and Pagination',
                    description: 'Test schools retrieval with pagination',
                    category: 'locality-management',
                    runTest: async (services, params) => {
                        const pageSize = params?.pageSize || 10;
                        const schools = await services.entityService.getSchools(1, pageSize);
                        return {
                            schoolCount: schools.totalResults,
                            pageSize,
                            schools: schools.data?.slice(0, 3)
                        };
                    },
                    requiredParams: ['pageSize']
                },
                {
                    id: 'buildings-retrieval',
                    name: 'Buildings Retrieval and Pagination',
                    description: 'Test buildings retrieval with pagination',
                    category: 'locality-management',
                    runTest: async (services, params) => {
                        const pageSize = params?.pageSize || 10;
                        const buildings = await services.entityService.getBuildings(1, pageSize);
                        return {
                            buildingCount: buildings.totalResults,
                            pageSize,
                            buildings: buildings.data?.slice(0, 3)
                        };
                    },
                    requiredParams: ['pageSize']
                },
                {
                    id: 'classes-retrieval',
                    name: 'Classes Retrieval and Pagination',
                    description: 'Test classes retrieval with pagination',
                    category: 'locality-management',
                    runTest: async (services, params) => {
                        const pageSize = params?.pageSize || 10;
                        const classes = await services.entityService.getClasses(1, pageSize);
                        return {
                            classCount: classes.totalResults,
                            pageSize,
                            classes: classes.data?.slice(0, 3)
                        };
                    },
                    requiredParams: ['pageSize']
                }
            ]
        },
        {
            id: 'group-management',
            name: 'Group Management',
            description: 'Groups retrieval, pagination, and group membership testing',
            icon: <IconBug size={20} />,
            tests: [
                {
                    id: 'groups-retrieval',
                    name: 'Groups Retrieval and Pagination',
                    description: 'Test groups retrieval with pagination',
                    category: 'group-management',
                    runTest: async (services, params) => {
                        const pageSize = params?.pageSize || 10;
                        const groups = await services.entityService.getGroups(1, pageSize);
                        return {
                            groupCount: groups.totalResults,
                            pageSize,
                            groups: groups.groups?.slice(0, 3)
                        };
                    },
                    requiredParams: ['pageSize']
                },
                {
                    id: 'group-membership',
                    name: 'Group Membership Testing',
                    description: 'Test group membership functionality',
                    category: 'group-management',
                    runTest: async (services) => {
                        const groups = await services.entityService.getGroups(1, 5);
                        const groupsWithMembers = groups.groups?.filter(g => g.Member && g.Member.length > 0) || [];
                        return {
                            totalGroups: groups.totalResults,
                            groupsWithMembers: groupsWithMembers.length,
                            sampleGroup: groupsWithMembers[0]
                        };
                    }
                }
            ]
        },
        {
            id: 'caching-performance',
            name: 'Caching & Performance',
            description: 'Cache clearing functionality, filter-specific caching, performance testing with concurrent requests',
            icon: <IconGauge size={20} />,
            tests: [
                {
                    id: 'cache-clearing',
                    name: 'Cache Clearing Functionality',
                    description: 'Test cache clearing functionality',
                    category: 'caching-performance',
                    runTest: async () => {
                        const startTime = Date.now();
                        await new Promise(resolve => setTimeout(resolve, 100));
                        const duration = Date.now() - startTime;
                        return {
                            cacheCleared: true,
                            duration,
                            timestamp: new Date().toISOString()
                        };
                    }
                },
                {
                    id: 'performance-testing',
                    name: 'Performance Testing with Concurrent Requests',
                    description: 'Test performance with concurrent API requests',
                    category: 'caching-performance',
                    runTest: async (services, params) => {
                        const concurrentRequests = params?.concurrentRequests || 3;
                        const startTime = Date.now();

                        const promises = Array(concurrentRequests).fill(0).map(() =>
                            services.userService.getUsers(1)
                        );

                        const results = await Promise.all(promises);
                        const duration = Date.now() - startTime;

                        return {
                            concurrentRequests,
                            duration,
                            successCount: results.filter(r => r.users).length,
                            totalUsers: results.reduce((sum, r) => sum + (r.totalResults || 0), 0)
                        };
                    },
                    requiredParams: ['concurrentRequests']
                }
            ]
        },
        {
            id: 'error-handling',
            name: 'Error Handling',
            description: 'Invalid user ID handling, malformed filter handling, network error simulation',
            icon: <IconAlertCircle size={20} />,
            tests: [
                {
                    id: 'invalid-user-id',
                    name: 'Invalid User ID Handling',
                    description: 'Test handling of invalid user IDs',
                    category: 'error-handling',
                    runTest: async (services) => {
                        try {
                            await services.userService.getUser('invalid-id-12345');
                            return { error: 'Expected error but got success' };
                        } catch (error) {
                            return {
                                expectedError: true,
                                errorMessage: error instanceof Error ? error.message : 'Unknown error',
                                handledCorrectly: true
                            };
                        }
                    }
                },
                {
                    id: 'malformed-filter',
                    name: 'Malformed Filter Handling',
                    description: 'Test handling of malformed SCIM filters',
                    category: 'error-handling',
                    runTest: async (services) => {
                        try {
                            await services.userService.getUsers(1, 'invalid filter syntax');
                            return { error: 'Expected error but got success' };
                        } catch (error) {
                            return {
                                expectedError: true,
                                errorMessage: error instanceof Error ? error.message : 'Unknown error',
                                handledCorrectly: true
                            };
                        }
                    }
                }
            ]
        },
        {
            id: 'util-controller',
            name: 'Util Controller',
            description: 'Util controller health check and test endpoint validation',
            icon: <IconDatabase size={20} />,
            tests: [
                {
                    id: 'util-health-check',
                    name: 'Util Controller Health Check',
                    description: 'Test the /util/health endpoint to verify the Util controller is operational',
                    category: 'util-controller',
                    runTest: async (services) => {
                        try {
                            const result = await services.corsApiService.healthCheck();
                            return {
                                success: true,
                                status: result?.status,
                                timestamp: result?.timestamp,
                                healthy: result?.status === 'healthy'
                            };
                        } catch (error) {
                            throw new Error(`Health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
                        }
                    }
                }
            ]
        },
        {
            id: 'mgmt-api',
            name: 'Mgmt API Surface',
            description: 'Smoke tests for current /mgmt endpoints (services, stats, build time, user settings, entitlements)',
            icon: <IconSettings size={20} />,
            tests: [
                {
                    id: 'mgmt-services',
                    name: 'Mgmt Services Catalog',
                    description: 'GET /mgmt/services should return an array of service descriptors',
                    category: 'mgmt-api',
                    runTest: async () => {
                        const scimConfig = getScimConfig();
                        const apiBase = scimConfig.apiUrl.endsWith('/')
                            ? scimConfig.apiUrl.slice(0, -1)
                            : scimConfig.apiUrl;
                        const token = await ScimAuthService.getAuthToken();
                        const response = await fetch(`${apiBase}/mgmt/services`, {
                            method: 'GET',
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json'
                            }
                        });
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }
                        const data = await response.json();
                        const isArray = Array.isArray(data);
                        return {
                            ok: true,
                            isArray,
                            count: isArray ? data.length : 0,
                            sample: isArray ? data.slice(0, 3) : data
                        };
                    }
                },
                {
                    id: 'mgmt-stats',
                    name: 'Mgmt Stats',
                    description: 'GET /mgmt/stats should return CPU usage stats (ApiCpuUsage/SqlCpuUsage)',
                    category: 'mgmt-api',
                    runTest: async () => {
                        const scimConfig = getScimConfig();
                        const apiBase = scimConfig.apiUrl.endsWith('/')
                            ? scimConfig.apiUrl.slice(0, -1)
                            : scimConfig.apiUrl;
                        const token = await ScimAuthService.getAuthToken();
                        const response = await fetch(`${apiBase}/mgmt/stats`, {
                            method: 'GET',
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json'
                            }
                        });
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }
                        const data = await response.json();
                        return {
                            ok: true,
                            ApiCpuUsage: (data as any)?.ApiCpuUsage,
                            SqlCpuUsage: (data as any)?.SqlCpuUsage
                        };
                    }
                },
                {
                    id: 'mgmt-buildtime',
                    name: 'Mgmt Build Time',
                    description: 'GET /mgmt/buildtime should return build time metadata',
                    category: 'mgmt-api',
                    runTest: async (services) => {
                        const data = await services.entityService.getBuildTime();
                        return {
                            ok: true,
                            keys: data && typeof data === 'object' ? Object.keys(data) : [],
                            data
                        };
                    }
                },
                {
                    id: 'mgmt-usersettings-roundtrip',
                    name: 'User Settings Roundtrip',
                    description: 'POST/GET/DELETE a temporary usersettings key (cleans up after itself)',
                    category: 'mgmt-api',
                    runTest: async () => {
                        const svc = getUserSettingsService();
                        const key = 'hades_scimApiTesting_smoke';
                        const value = { value: { ts: new Date().toISOString(), note: 'temporary test value' } } as const;
                        try {
                            await svc.set(key, value.value as any);
                            const fetched = await svc.getRaw(key);
                            return {
                                ok: true,
                                key,
                                fetched
                            };
                        } finally {
                            // Cleanup best-effort so repeated runs don't accumulate noise.
                            try { await svc.delete(key); } catch { /* ignore */ }
                        }
                    }
                },
                {
                    id: 'mgmt-entitlements-tier1',
                    name: 'Entitlements (Tier1)',
                    description: 'GET /mgmt/Entitlements/entity should return requestor + entitlements list',
                    category: 'mgmt-api',
                    runTest: async () => {
                        const data = await entitlementsService.fetchEntityEntitlements(true);
                        const entitlements = (data as any)?.EntityEntitlements;
                        return {
                            ok: true,
                            requestor: (data as any)?.Requestor,
                            isAdministrator: (data as any)?.IsAdministrator,
                            entitlementCount: Array.isArray(entitlements) ? entitlements.length : 0,
                            sample: Array.isArray(entitlements) ? entitlements.slice(0, 5) : entitlements
                        };
                    }
                }
            ]
        }
    ], []);

    // Initialize test results
    React.useEffect(() => {
        const initialResults: TestResult[] = [];
        testCategories.forEach(category => {
            category.tests.forEach(test => {
                initialResults.push({
                    id: test.id,
                    name: test.name,
                    status: 'pending'
                });
            });
        });
        setTestResults(initialResults);
    }, [testCategories]);

    // Initialize default test parameters
    React.useEffect(() => {
        const defaultParams: Record<string, any> = {};
        testCategories.forEach(category => {
            category.tests.forEach(test => {
                if (test.requiredParams) {
                    test.requiredParams.forEach(param => {
                        const key = `${test.id}-${param}`;
                        if (param === 'pageSize') defaultParams[key] = 10;
                        if (param === 'entityType') defaultParams[key] = 'Employee';
                        if (param === 'searchTerm') defaultParams[key] = 'john';
                        if (param === 'concurrentRequests') defaultParams[key] = 3;
                    });
                }
            });
        });
        setTestParams(defaultParams);
    }, [testCategories]);

    // Update test result
    const updateTestResult = useCallback((testId: string, updates: Partial<TestResult>) => {
        setTestResults(prev => prev.map(result =>
            result.id === testId ? { ...result, ...updates } : result
        ));
    }, []);

    // Run individual test
    const runTest = useCallback(async (test: TestDefinition) => {
        if (runningTests.has(test.id)) return;

        setRunningTests(prev => new Set(prev).add(test.id));
        updateTestResult(test.id, { status: 'running', timestamp: new Date().toISOString() });

        try {
            const startTime = Date.now();
            const result = await test.runTest({
                userService,
                schemaService,
                entityService,
                testingService,
                authService,
                corsApiService
            }, testParams);
            const duration = Date.now() - startTime;

            updateTestResult(test.id, {
                status: 'passed',
                duration,
                result,
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            updateTestResult(test.id, {
                status: 'failed',
                error: error instanceof Error ? error.message : 'Unknown error',
                timestamp: new Date().toISOString()
            });
        } finally {
            setRunningTests(prev => {
                const newSet = new Set(prev);
                newSet.delete(test.id);
                return newSet;
            });
        }
    }, [userService, schemaService, entityService, testingService, testParams, runningTests, updateTestResult]);

    // Run all tests in a category
    const runCategoryTests = useCallback(async (categoryId: string) => {
        const category = testCategories.find(c => c.id === categoryId);
        if (!category) return;

        for (const test of category.tests) {
            await runTest(test);
        }
    }, [testCategories, runTest]);

    // Run all tests
    const runAllTests = useCallback(async () => {
        for (const category of testCategories) {
            for (const test of category.tests) {
                await runTest(test);
            }
        }
    }, [testCategories, runTest]);

    // Clear all test results
    const clearResults = useCallback(() => {
        setTestResults(prev => prev.map(result => ({ ...result, status: 'pending' as const })));
    }, []);

    // Get test result
    const getTestResult = useCallback((testId: string) => {
        return testResults.find(r => r.id === testId);
    }, [testResults]);

    // Get status icon
    const getStatusIcon = (status: TestResult['status']) => {
        switch (status) {
            case 'pending': return <IconInfoCircle size={20} color="var(--mantine-color-dimmed)" />;
            case 'running': return <Loader size={20} />;
            case 'passed': return <IconCircleCheck size={20} color="var(--mantine-color-green-6)" />;
            case 'failed': return <IconX size={20} color="var(--mantine-color-red-6)" />;
            case 'skipped': return <IconInfoCircle size={20} color="var(--mantine-color-dimmed)" />;
            default: return <IconInfoCircle size={20} color="var(--mantine-color-dimmed)" />;
        }
    };

    // Get status color
    const getStatusColor = (status: TestResult['status']) => {
        switch (status) {
            case 'pending': return 'gray';
            case 'running': return 'blue';
            case 'passed': return 'green';
            case 'failed': return 'red';
            case 'skipped': return 'gray';
            default: return 'gray';
        }
    };

    return (
        <Box p="lg">
            <Title order={1} ta="center" mb="md">
                SCIM API Testing Dashboard
            </Title>

            {/* Control Panel */}
            <Paper p="lg" mb="lg" withBorder>
                <SimpleGrid cols={{ base: 1, md: 2 }}>
                    <Stack gap="xs">
                        <Title order={5}>Test Controls</Title>
                        <Group gap="sm" wrap="wrap">
                            <Button
                                onClick={runAllTests}
                                disabled={runningTests.size > 0}
                                leftSection={<IconPlayerPlay size={18} />}
                            >
                                Run All Tests
                            </Button>
                            <Button
                                variant="outline"
                                onClick={clearResults}
                                leftSection={<IconX size={18} />}
                            >
                                Clear Results
                            </Button>
                        </Group>
                    </Stack>
                </SimpleGrid>
            </Paper>

            {/* Test Categories */}
            <Stack gap="lg">
                {testCategories.map((category) => (
                    <Paper key={category.id} withBorder p="md">
                        <Group justify="space-between" align="flex-start" mb="md">
                            <Group>
                                {category.icon}
                                <Title order={5}>{category.name}</Title>
                            </Group>
                            <Button
                                variant="outline"
                                size="xs"
                                onClick={() => runCategoryTests(category.id)}
                                disabled={runningTests.size > 0}
                                leftSection={<IconPlayerPlay size={14} />}
                            >
                                Run All {category.name} Tests
                            </Button>
                        </Group>
                        <Text size="sm" c="dimmed" mb="md">
                            {category.description}
                        </Text>

                        <Accordion value={expandedCategory || ''} onChange={setExpandedCategory}>
                            {category.tests.map((test) => {
                                const result = getTestResult(test.id);
                                const isRunning = runningTests.has(test.id);

                                return (
                                    <Accordion.Item key={test.id} value={test.id}>
                                        <Accordion.Control>
                                            <Group justify="space-between" wrap="nowrap">
                                                <Group gap="xs" miw={200}>
                                                    {getStatusIcon(result?.status || 'pending')}
                                                    <Text size="sm">{test.name}</Text>
                                                </Group>
                                                <Group gap="xs">
                                                    {result?.duration && (
                                                        <Badge variant="outline" size="sm">
                                                            {result.duration}ms
                                                        </Badge>
                                                    )}
                                                    <Badge color={getStatusColor(result?.status || 'pending')} size="sm">
                                                        {result?.status || 'pending'}
                                                    </Badge>
                                                </Group>
                                            </Group>
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                            <Stack gap="md">
                                                <Text size="sm" c="dimmed">
                                                    {test.description}
                                                </Text>

                                                {/* Test Parameters */}
                                                {test.requiredParams && test.requiredParams.length > 0 && (
                                                    <Stack gap="xs">
                                                        <Text size="sm" fw={500}>Test Parameters:</Text>
                                                        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
                                                            {test.requiredParams.map((param) => {
                                                                const key = `${test.id}-${param}`;
                                                                const value = testParams[key] || '';

                                                                return (
                                                                    <TextInput
                                                                        key={param}
                                                                        label={param}
                                                                        value={String(value)}
                                                                        onChange={(e) => setTestParams(prev => ({
                                                                            ...prev,
                                                                            [key]: e.currentTarget.value
                                                                        }))}
                                                                        size="sm"
                                                                    />
                                                                );
                                                            })}
                                                        </SimpleGrid>
                                                    </Stack>
                                                )}

                                                {/* Test Actions */}
                                                <Button
                                                    onClick={() => runTest(test)}
                                                    loading={isRunning}
                                                    leftSection={<IconPlayerPlay size={18} />}
                                                >
                                                    {isRunning ? 'Running...' : 'Run Test'}
                                                </Button>

                                                {/* Test Results */}
                                                {result && result.status !== 'pending' && (
                                                    <Stack gap="xs">
                                                        <Divider />
                                                        <Text size="sm" fw={500}>Test Results:</Text>

                                                        {result.status === 'failed' && result.error && (
                                                            <Alert color="red">
                                                                {result.error}
                                                            </Alert>
                                                        )}

                                                        {result.status === 'passed' && result.result && (
                                                            <Paper p="md" withBorder>
                                                                <Text
                                                                    size="sm"
                                                                    component="pre"
                                                                    style={{
                                                                        whiteSpace: 'pre-wrap',
                                                                        fontSize: '0.875rem',
                                                                        fontFamily: 'monospace',
                                                                        margin: 0
                                                                    }}
                                                                >
                                                                    {JSON.stringify(result.result, null, 2)}
                                                                </Text>
                                                            </Paper>
                                                        )}
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                );
                            })}
                        </Accordion>
                    </Paper>
                ))}
            </Stack>

            {/* Results Summary Table */}
            <Paper p="lg" mt="lg" withBorder>
                <Title order={5} mb="md">Test Results Summary</Title>
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Test Name</Table.Th>
                            <Table.Th>Category</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Duration</Table.Th>
                            <Table.Th>Timestamp</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {testResults.map((result) => (
                            <Table.Tr key={result.id}>
                                <Table.Td>{result.name}</Table.Td>
                                <Table.Td>
                                    {testCategories.find(c =>
                                        c.tests.some(t => t.id === result.id)
                                    )?.name || 'Unknown'}
                                </Table.Td>
                                <Table.Td>
                                    <Badge color={getStatusColor(result.status)} size="sm">
                                        {result.status}
                                    </Badge>
                                </Table.Td>
                                <Table.Td>
                                    {result.duration ? `${result.duration}ms` : '-'}
                                </Table.Td>
                                <Table.Td>
                                    {result.timestamp ? formatTimeShort(result.timestamp) : '-'}
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Paper>
        </Box>
    );
};
